import { SyntheticEvent, useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'

import { breadcrumbTransformer } from '../models/Breadcrumb/breadcrumbTransformer'
import { colors } from '../theme'
import { tagCommanderTransformer } from '../models/TagCommander/tagCommanderTransformer'
import {
  OnboardingStepNames,
  OnboardingStepValues,
  TcClickTypes,
  TcFormNames,
  TcPageTemplateNames,
} from '../configuration/tagcommander'
import tcClick from '../models/tcClick/tcClick'
import { UserState } from '../redux/user/redux'
import { OnboardingTemplateProps } from '../templates/Onboarding'
import {
  onboardingCoverTransformer,
  onboardingStepsTransformer,
} from '../models/Onboarding'
import { actions, selectors } from '../redux'
import {
  actions as OnboardingActions,
  OnboardingState,
} from '../redux/onboarding/redux'
import { NutritionalPreferences, NutritionalWeight } from '../types'
import { onboardingSeoTransformer } from '../models/Onboarding/OnboardingSeo'

import { useSsoUrl } from './useSsoUrl'
import usePWAInstallPrompt from './usePWAInstallPrompt'

const useOnboarding = () => {
  usePWAInstallPrompt()
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const userInit = useSelector(selectors.user.isInit)
  useEffect(() => {
    if (userInit) {
      dispatch(actions.app.setIsLoading(false))
    }
  }, [userInit, dispatch])

  const stepsValues: OnboardingState['stepsValues'] = useSelector(
    selectors.onboarding.stepsValues
  )
  const currentStepIndex: OnboardingState['currentStepIndex'] = useSelector(
    selectors.onboarding.currentStepIndex
  )
  const tips: OnboardingState['tips'] = useSelector(selectors.onboarding.tips)
  const stepsWeight: OnboardingState['stepsWeight'] = useSelector(
    selectors.onboarding.stepsWeight
  )
  const isKeycloakProcessDone: UserState['isKeycloakProcessDone'] = useSelector(
    selectors.user.isKeycloakProcessDone
  )

  const isKeycloakLoggedIn: UserState['keycloakToken'] = useSelector(
    selectors.user.keycloakToken
  )

  const ssoLoginUrl = useSsoUrl('fam_find')

  const weightMessage = useMemo(() => {
    const allValues = Object.values(stepsWeight)
    const sum = allValues
      .map((nutri) => {
        if (nutri === NutritionalWeight.MINIMAL) {
          return 0.5
        } else if (nutri === NutritionalWeight.MEDIUM) {
          return 0.75
        } else if (nutri === NutritionalWeight.OPTIMAL) {
          return 1
        } else {
          return 0
        }
      })
      .reduce((a: number, b: number) => a + b, 0)

    const ratio = sum / (allValues?.length ?? 1)
    switch (true) {
      case ratio <= 2 / 3:
        return NutritionalWeight.MINIMAL
      case ratio > 2 / 3 && ratio < 0.832:
        return NutritionalWeight.MEDIUM
      case ratio > 0.832:
        return NutritionalWeight.OPTIMAL
      default:
        return NutritionalWeight.MINIMAL
    }
  }, [stepsWeight])

  const steps = useMemo(
    () => onboardingStepsTransformer(t, stepsValues, weightMessage, tips),
    [t, stepsValues, tips, weightMessage]
  )

  const handleComplete = useCallback(
    (step) => {
      dispatch(
        OnboardingActions.onboardRequest({
          params: {
            steps: steps?.map((step: any) => step.name),
            currentStepIndex: step,
          },
        })
      )
      // todo : generate planning after onboarding
    },
    [dispatch, steps]
  )

  const handleNextStep = useCallback(
    (ev?: SyntheticEvent) => {
      const newIdx =
        currentStepIndex + 1 <= steps.length - 1
          ? currentStepIndex + 1
          : steps.length - 1

      // Last question submit
      if (currentStepIndex === steps.length - 2) {
        ev &&
          tcClick({
            click_name: `${t('onboarding_congratulations_cta')}_${
              TcPageTemplateNames.Another
            }_fam`,
            click_type: TcClickTypes.action,
            chapter1: `questions menu personnalisé fam`,
            chapter2: t('breadcrumb_eat_better'),
            chapter3: t('breadcrumb_fam'),
          })
        return handleComplete(currentStepIndex + 1)
        // return dispatch(UserActions?.generatePlanning())
      }

      dispatch(OnboardingActions?.setCurrentStepIndex(newIdx))
    },
    [currentStepIndex, steps.length, dispatch, t, handleComplete]
  )

  const handlePreviousStep = useCallback(() => {
    const newIdx = currentStepIndex - 1 >= 0 ? currentStepIndex - 1 : 0
    dispatch(OnboardingActions?.setCurrentStepIndex(newIdx))
  }, [currentStepIndex, dispatch])

  const handleOnChange = useCallback(
    (name?: string, value?: string | number | boolean) => {
      dispatch(
        OnboardingActions?.setStepValues({
          name,
          value,
        })
      )

      const selectedValue = steps[currentStepIndex].options?.find(
        (o: any) => o.id === value
      )

      if (selectedValue?.weight) {
        dispatch(
          OnboardingActions?.setStepWeight({
            name,
            weight: selectedValue?.weight,
          })
        )
      }
    },
    [dispatch, steps, currentStepIndex]
  )

  const handleQuitOnboarding = useCallback(() => {
    handleComplete(currentStepIndex)
  }, [handleComplete, currentStepIndex])

  const breadcrumbConfig = breadcrumbTransformer({
    bgColor: colors.greenMint,
    t,
    lastItems: currentStepIndex
      ? [
          {
            text: t('breadcrumb_quiz'),
          },
        ]
      : [],
  })

  const onboardingTemplateProps: OnboardingTemplateProps = useMemo(
    () => ({
      seoProps: onboardingSeoTransformer(t),
      coverSectionProps: onboardingCoverTransformer(t, breadcrumbConfig),
      onboardingSectionProps: {
        steps,
        stepsValues,
        currentStepIndex,
        handlePreviousStep,
        handleNextStep,
        handleOnChange,
        isKeycloakProcessDone,
        backButtonProps: {
          text: t('onboarding_back_cta_label'),
          icon: 'backArrow',
          variantType: 'button',
          actionProps: {
            type: 'button',
            onClick: (ev?: SyntheticEvent) => {
              ev &&
                tcClick({
                  click_name: `${t('onboarding_back_cta_label')}_${
                    TcFormNames.Onboarding
                  }_${OnboardingStepNames[currentStepIndex - 1]}fam`,
                  click_type: TcClickTypes.action,
                  chapter1: `questions menu personnalisé fam`,
                  chapter2: t('breadcrumb_eat_better'),
                  chapter3: currentStepIndex
                    ? t('breadcrumb_quiz')
                    : t('breadcrumb_fam'),
                })
              handlePreviousStep()
            },
          },
        },
        nextButtonProps: {
          text: t('onboarding_next_cta_label'),
          variantType: 'button',
          variantColorScheme: 'black',
          actionProps: { type: 'button', onClick: handleNextStep },
        },
        loginButtonProps:
          !isKeycloakLoggedIn && isKeycloakProcessDone
            ? {
                text: t('onboarding_login_cta_label'),
                variantType: 'link',
                variantColorScheme: 'white',
                actionProps: {
                  href: ssoLoginUrl,
                  target: '_self',
                  onClickHandler: () => {
                    tcClick({
                      click_name: `amorcer_${t('onboarding_login_cta_label')}_${
                        TcPageTemplateNames.Another
                      }_fam`,
                      click_type: TcClickTypes.action,
                      chapter1: `action mon compte fam`,
                      chapter2: t('breadcrumb_eat_better'),
                      chapter3: currentStepIndex
                        ? t('breadcrumb_quiz')
                        : t('breadcrumb_fam'),
                    })
                  },
                },
              }
            : undefined,
        startButtonProps: {
          text: t('onboarding_start_cta_label'),
          variantType: 'button',
          variantColorScheme: 'black',
          actionProps: {
            type: 'button',
            onClick: (ev?: SyntheticEvent) => {
              ev &&
                tcClick({
                  click_name: `amorcer_${t('onboarding_start_cta_label')}_fam`,
                  click_type: TcClickTypes.action,
                  chapter1: `questions menu personnalisé fam`,
                  chapter2: t('breadcrumb_eat_better'),
                  chapter3: currentStepIndex
                    ? t('breadcrumb_quiz')
                    : t('breadcrumb_fam'),
                })
              handleNextStep()
            },
          },
        },
        quitButtonProps: {
          text: t('onboarding_quit_cta_label'),
          onClickHandler: (ev?: SyntheticEvent) => {
            ev &&
              tcClick({
                click_name: `${t('onboarding_quit_cta_label')}_fam`,
                click_type: TcClickTypes.action,
                chapter1: `questions menu personnalisé fam`,
                chapter2: t('breadcrumb_eat_better'),
                chapter3: currentStepIndex
                  ? t('breadcrumb_quiz')
                  : t('breadcrumb_fam'),
              })
            handleQuitOnboarding()
          },
        },
      },
      mobileBreadcrumbProps: breadcrumbConfig,
      tcProps: tagCommanderTransformer({
        pageName: currentStepIndex
          ? t('tracking_page_onboarding')
          : t('tracking_page_chapter_3'),
        tcTemplate: TcPageTemplateNames.Form,
        tcCAt1: t('tracking_page_chapter_1'),
        tcCAt2: t('tracking_page_chapter_2'),
        tcCAt3: currentStepIndex ? t('tracking_page_chapter_3') : undefined,
        formData: currentStepIndex
          ? {
              formName: TcFormNames.Onboarding,
              hasStep: true,
              stepNumber: currentStepIndex,
              stepName: OnboardingStepNames[currentStepIndex - 1],
              isConfirmation: currentStepIndex === 13,
            }
          : null,
        ...(currentStepIndex >= 2 &&
          // do not track transition step
          currentStepIndex !== 7 && {
            extraData: {
              [OnboardingStepValues[
                currentStepIndex < 7
                  ? currentStepIndex - 2
                  : currentStepIndex - 3
              ]]:
                stepsValues[
                  Object.values(NutritionalPreferences)[
                    currentStepIndex < 7
                      ? currentStepIndex - 2
                      : currentStepIndex - 3
                  ]
                ],
            },
          }),
        triggerTcUpdate: currentStepIndex,
      }),
    }),
    [
      t,
      breadcrumbConfig,
      steps,
      stepsValues,
      currentStepIndex,
      handlePreviousStep,
      handleNextStep,
      handleOnChange,
      isKeycloakProcessDone,
      isKeycloakLoggedIn,
      ssoLoginUrl,
      handleQuitOnboarding,
    ]
  )

  return { onboardingTemplateProps }
}

export default useOnboarding
